import { useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';
import moment from 'moment';
import { CHECK_PERIOD_LOCK } from '../../../../../../graphql/accountingPeriod';
import {
  dateFallsInPeriod,
  useOnAccountingPeriodChanged,
  useRefreshWhenVisible,
} from '../../../../../../utils/accountingPeriodEvents';

const DATE_FORMATS = ['YYYY-MM-DD', 'DD/MM/YYYY'];

function dateToCheckIso(stringDate) {
  if (!stringDate) return null;
  const m = moment(stringDate, DATE_FORMATS, true);
  const safe = m.isValid() ? m : moment(stringDate);
  if (!safe.isValid()) return null;
  return new Date(safe.year(), safe.month(), safe.date(), 15, 0, 0).toISOString();
}

export function useDraftPeriodLocks(companyId, stringDates, enabled) {
  const client = useClient();
  const [locksByDate, setLocksByDate] = useState({});
  const [forcedByDate, setForcedByDate] = useState({});
  const [fetching, setFetching] = useState(false);
  const [revision, setRevision] = useState(0);
  const fetchGen = useRef(0);
  const locksRef = useRef(locksByDate);
  locksRef.current = locksByDate;

  const bump = () => setRevision((current) => current + 1);

  useOnAccountingPeriodChanged(enabled ? companyId : null, (message) => {
    if (message?.month && message?.year && typeof message.opened === 'boolean') {
      setForcedByDate((prev) => {
        const next = { ...prev };
        let changed = false;
        (stringDates || []).filter(Boolean).forEach((stringDate) => {
          const iso = dateToCheckIso(stringDate);
          if (!iso || !dateFallsInPeriod(iso, message.month, message.year)) return;
          next[stringDate] = message.opened
            ? { isLocked: false, message: '' }
            : {
              isLocked: true,
              message: locksRef.current[stringDate]?.message || prev[stringDate]?.message || '',
            };
          changed = true;
        });
        return changed ? next : prev;
      });
    }
    bump();
  }, 'ENTRIES');

  useRefreshWhenVisible(Boolean(enabled && companyId), bump);

  const datesKey = useMemo(() => {
    const unique = [...new Set((stringDates || []).filter(Boolean))];
    unique.sort();
    return unique.join('|');
  }, [stringDates]);

  useEffect(() => {
    if (!enabled || !companyId || !datesKey) {
      setLocksByDate({});
      setForcedByDate({});
      setFetching(false);
      return undefined;
    }

    const uniqueDates = datesKey.split('|');
    const gen = ++fetchGen.current;
    let cancelled = false;
    setFetching(true);

    (async () => {
      const results = {};
      await Promise.all(
        uniqueDates.map(async (stringDate) => {
          const iso = dateToCheckIso(stringDate);
          if (!iso) {
            results[stringDate] = { isLocked: false, message: '' };
            return;
          }
          try {
            const result = await client
              .query(
                CHECK_PERIOD_LOCK,
                {
                  companyId,
                  date: iso,
                  periodType: 'ENTRIES',
                },
                { requestPolicy: 'network-only' }
              )
              .toPromise();
            results[stringDate] = result.data?.checkPeriodLock ?? {
              isLocked: false,
              message: '',
            };
          } catch {
            results[stringDate] = { isLocked: false, message: '' };
          }
        })
      );

      if (!cancelled && gen === fetchGen.current) {
        setLocksByDate(results);
        setForcedByDate((prev) => {
          const next = { ...prev };
          let changed = false;
          Object.keys(prev).forEach((stringDate) => {
            if (results[stringDate]?.isLocked === prev[stringDate]?.isLocked) {
              delete next[stringDate];
              changed = true;
            }
          });
          return changed ? next : prev;
        });
        setFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, companyId, datesKey, enabled, revision]);

  const getLockForDate = (stringDate) =>
    forcedByDate[stringDate] ?? locksByDate[stringDate] ?? { isLocked: false, message: '' };

  const waitingForFirstAnswer = fetching
    && Object.keys(locksByDate).length === 0
    && Object.keys(forcedByDate).length === 0;

  return { locksByDate, fetching: waitingForFirstAnswer, getLockForDate };
}
